import React, { useEffect, useState } from "react";
import { Mail, Send, AlertTriangle, CheckCircle2, Clock, CalendarDays, Sunrise, Heart, Trophy, Award, FileText, Mic, Star } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import api, { getErrorMessage } from "../lib/api";
import EmailDomainStatus from "./EmailDomainStatus";
import { useProgress } from "../context/ProgressContext";

const REASONS = { no_children: "Add a child first.", no_certificates: "No certificates earned yet.", no_lesson_day_set: "Set a lesson day on a child's card first.", nothing_due: "No child is due today.", not_following: "You aren't following any children yet." };

// One email section: title, description, on/off switch, "send now" button and last-sent info.
const EmailSection = ({ icon: Icon, tint, color, title, description, field, sendPath, sendLabel, lastSentAt, settings, setSettings, result, testId, first = false, noSend = false }) => {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const toggle = async (on) => {
    try {
      const r = await api.put("/account/settings", { [field]: on });
      setSettings(r.data);
      toast.success(`${title} ${on ? "on" : "off"}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not update setting"));
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(sendPath);
      setLast(r.data);
      if (r.data.sent) toast.success(`Sent to ${settings.email}`, { description: r.data.fallback ? "Sent from the Resend test sender." : `Sent from ${r.data.sender}` });
      else toast.error(r.data.email_error ? "Email could not be delivered" : "Nothing to send yet", { description: r.data.email_error || REASONS[r.data.reason] || "" });
      const s = await api.get("/account/settings");
      setSettings(s.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not send email"));
    } finally { setBusy(false); }
  };

  return (
    <div className={first ? "" : "mt-6 border-t border-[#e9f9fd] pt-6"} data-testid={`${testId}-settings`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: tint, color }}><Icon /></span>
        <div className="flex-1">
          <h3 className="font-bold text-[#0c4a6e] text-xl" style={{ fontFamily: "Literata, serif" }}>{title}</h3>
          <p className="text-gray-600 text-sm mt-1">{description}</p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span className="text-sm font-bold text-[#0c4a6e]">{settings[field] ? "On" : "Off"}</span>
          <Switch checked={settings[field]} onCheckedChange={toggle} style={settings[field] ? { backgroundColor: color } : {}} data-testid={`${testId}-toggle`} />
        </label>
      </div>
      {!noSend && <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <button onClick={send} disabled={busy} className="btn-pill text-white inline-flex items-center gap-2 disabled:opacity-60" style={{ background: color }} data-testid={`send-test-${testId}`}>
          <Send size={16} /> {busy ? "Sending..." : sendLabel}
        </button>
        {lastSentAt && <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Clock size={14} /> Last sent {new Date(lastSentAt).toLocaleString()}</span>}
      </div>}
      {last?.sent && <p className="mt-3 text-sm text-[#0d4d33] inline-flex items-center gap-1" data-testid={`${testId}-result`}><CheckCircle2 size={16} className="text-[#2e8b57]" /> {result(last)}</p>}
    </div>
  );
};

// Parent-facing email settings on the Family dashboard (signed-in users only).
const ReminderSettings = () => {
  const [settings, setSettings] = useState(null);
  const { children, following } = useProgress();

  useEffect(() => {
    api.get("/account/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  }, []);

  if (!settings) return null;
  const common = { settings, setSettings };
  const hasChildren = children.length > 0;

  return (
    <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8" data-testid="email-settings">
      {hasChildren && (
        <>
          <EmailSection {...common} first icon={Trophy} tint="#fff8e1" color="#f7931e" testId="reminder" field="reminder_emails" sendPath="/reminders/send-test" sendLabel="Send me this week's email now"
            title="Sunday family email"
            description={<>Every <span className="font-semibold">{settings.reminder_schedule}</span> to <span className="font-semibold">{settings.email}</span>: the family bamboo board (who's leading this week) plus each child's streak status.</>}
            lastSentAt={settings.last_reminder_sent_at}
            result={(r) => (r.standings?.[0]?.week > 0 ? `${r.standings[0].name} is leading this week` : "Standings sent - nobody has bamboo yet this week") + (r.at_risk?.length ? ` · ${r.at_risk.map((c) => c.name).join(", ")} at risk` : "") + "."} />
          <EmailSection {...common} icon={Sunrise} tint="#ffe4ec" color="#e5356f" testId="nudge" field="lesson_nudges" sendPath="/nudges/send-test" sendLabel="Send me a nudge now"
            title="Lesson-day nudge"
            description={<>At <span className="font-semibold">{settings.nudge_schedule}</span>, only if that child hasn't done a step yet. Paused automatically while the Sunday email is off.</>}
            lastSentAt={settings.last_nudge_sent_at}
            result={(r) => `Nudge sent for ${r.children.map((c) => c.name).join(", ")}.`} />
          <EmailSection {...common} icon={CalendarDays} tint="#f3e8ff" color="#7d5fb2" testId="report" field="monthly_reports" sendPath="/reports/send-test" sendLabel="Email me this month's report now"
            title="Monthly progress report"
            description={<>On the <span className="font-semibold">{settings.report_schedule}</span>: each child's lessons, quiz scores, certificates, active days and streak for the past month.</>}
            lastSentAt={settings.last_report_sent_at}
            result={(r) => `${r.month} report sent for ${r.reports.map((x) => x.name).join(", ")}.`} />
          <EmailSection {...common} icon={Mic} tint="#fff0f5" color="#e5356f" testId="voice-alert" field="voice_alert_emails" noSend
            title="Voice cheer alerts"
            description={<>The moment a grandparent or mentor leaves a <span className="font-semibold">voice cheer</span>, parents and co-parents get a short email with a one-tap "Play the voice cheer" link.</>}
            lastSentAt={settings.last_voice_alert_sent_at}
            result={() => ""} />
          <EmailSection {...common} icon={FileText} tint="#e9f9fd" color="#1b8bc9" testId="report-card" field="report_card_emails" sendPath="/report-cards/send-test" sendLabel="Email me this year's cards now"
            title="Yearly report card"
            description={<>Every <span className="font-semibold">{settings.report_card_schedule}</span>: one email per child with last year's report card as an image - lessons, quiz scores, streaks, goal badge, stickers and cheers. You can also email any card from its page.</>}
            lastSentAt={settings.last_report_card_sent_at}
            result={(r) => `${r.year} report card${r.cards.length === 1 ? "" : "s"} sent for ${r.cards.map((x) => x.child).join(", ")}.`} />
        </>
      )}
      {(hasChildren || following.length > 0) && (
        <EmailSection {...common} first={!hasChildren} icon={Award} tint="#fff8e1" color="#c9a227" testId="certificate" field="certificate_emails" sendPath="/certificates/send-test" sendLabel="Email me the latest certificate" noSend={!hasChildren}
          title="Certificate emails"
          description={<>The moment a child finishes all six steps of a lesson, {hasChildren ? "everyone in the family - parents and followers - gets" : "you get"} the certificate as an image plus a lesson recap (steps, quiz score, big idea), ready to print or share.</>}
          lastSentAt={settings.last_certificate_sent_at}
          result={(r) => `${r.child}'s Lesson ${r.lesson} certificate sent${r.sample ? " (sample)" : ""}.`} />
      )}
      {following.length > 0 && (
        <EmailSection {...common} icon={Star} tint="#e8f8ef" color="#2e8b57" testId="reaction" field="reaction_emails" noSend
          title="Cheer reactions"
          description={<>When a child stars or replies to one of your cheers, you get a short email with your original note quoted - so you know it landed.</>}
          lastSentAt={settings.last_reaction_sent_at}
          result={() => ""} />
      )}
      {following.length > 0 && (
        <EmailSection {...common} icon={Heart} tint="#fff0f5" color="#c92a5e" testId="digest" field="follower_digest" sendPath="/digests/send-test" sendLabel="Email me the digest now"
          title="Monthly follower digest"
          description={<>On the <span className="font-semibold">{settings.digest_schedule}</span>: how the children you follow did last month - lessons, quizzes, streaks, goal badges, new certificates, your cheers and your latest cheer photo.</>}
          lastSentAt={settings.last_digest_sent_at}
          result={(r) => `${r.month} digest sent for ${r.families.flatMap((f) => f.children.map((c) => c.name)).join(", ")}.`} />
      )}
      {!hasChildren && following.length === 0 && (
        <p className="text-sm text-gray-500 inline-flex items-center gap-2"><Mail size={16} /> Email updates appear here once you add a child or follow one.</p>
      )}

      {settings.fallback_active && (
        <div className="mt-4 rounded-2xl bg-[#fff8e1] border border-[#f3c932] p-4 text-sm text-[#5a4600] flex gap-3" data-testid="sender-warning">
          <AlertTriangle className="flex-shrink-0 text-[#f7931e]" size={18} />
          <div>
            <p className="font-bold">Last email could not be sent from the main sender</p>
            <p className="mt-1">Emails are currently sent from <span className="font-mono">{settings.sender_in_use}</span>. Check the sending-domain status below.</p>
            {settings.last_email_error && <p className="mt-2 text-xs text-[#8a6d00] break-words">Last error: {settings.last_email_error}</p>}
          </div>
        </div>
      )}
      <EmailDomainStatus />
    </div>
  );
};

export default ReminderSettings;
